<script setup lang="ts">
	import { toRaw } from 'vue'

	const open = ref(false)
	const { settings, applySettings, saveSettings, resetSettings } = useAccessibilitySettings()

	const cloneSettings = (value) => {
		return structuredClone(toRaw(value))
	}

	const draftSettings = ref(cloneSettings(settings.value))

	const openModal = () => {
		draftSettings.value = cloneSettings(settings.value)
		open.value = true
	}

	const closeModal = () => {
  		draftSettings.value = cloneSettings(settings.value)
		applySettings(settings.value)
  		open.value = false
	}

	const saveAndClose = () => {
		settings.value = cloneSettings(draftSettings.value)
		applySettings(settings.value)
		saveSettings()
		open.value = false
	}

	const resetAndClose = () => {
		resetSettings()
		draftSettings.value = cloneSettings(settings.value)
		open.value = false
	}

	watch(
		draftSettings, (value) => {
			applySettings(value)
		}, { deep: true }
	)
</script>

<template>
	<section aria-label="Einstellungen für die Barrierefreiheit" class="fixed bottom-10 left-5 sm:left-10 z-50 flex items-center">
		<UModal
		 	v-model:open="open"
		 	@update:open="value => {
    			if (!value) {
      				closeModal()
      				open = false
   				}
			 }"
			:ui="{
			 	overlay: 'bg-black/20 backdrop-blur-sm md:backdrop-blur-none',
				content: 'bg-white rounded-none xs:rounded-2xl shadow-2xl w-[95%] sm:max-w-4/5 md:max-w-2/3 lg:max-w-1/2',
				body: 'p-0!',
				header: 'bg-white px-4 lg:px-5 [&_h2]:text-gray-800 [&_h2]:mb-0 [&_button]:cursor-pointer [&_.iconify]:me-0! [&_.iconify]:size-8 lg:[&_.iconify]:size-10'
			}"
		>
			<UButton @click="openModal" variant="solid" color="info" class="rounded-full p-0! size-14 md:size-16 flex my-auto items-center" title="Einstellungen für Barrierefreiheit" aria-label="Einstellungen für die Barrierefreiheit öffnen">
				<GsIcon name="proicons:accessibility" class="size-8 lg:size-10 me-0!"></GsIcon>
			</UButton>

			<template #header>
			 	<div class="flex w-full items-center justify-between gap-4">
					<div class="flex items-center gap-2">
						<GsIcon name="proicons:accessibility" class="hidden xs:inline text-primary flex-none size-8 lg:size-10" />
						<h2 class="mb-0">Anzeige & Lesbarkeit anpassen</h2>
					</div>
					<UButton icon="i-lucide-x" color="neutral" variant="link" class="ring-0! hover:bg-transparent!" aria-label="Schließen" @click="closeModal" />
				</div>
			</template>

			<template #body>
				<div class="p-4 lg:p-5">
					<p class="mb-8" data-nosnippet>Mit diesen Einstellungen können Sie Lesbarkeit und Darstellung der Website an Ihre persönlichen Anforderungen anpassen. Alle Änderungen werden unmittelbar angewendet.</p>
					<AccessibilitySettings v-model="draftSettings" />
					<div class="flex gap-3.5 mt-8 md:justify-end">
						<GsBtn label="Speichern" @click="saveAndClose" size="lg" icon="material-symbols:save" variant="solid" color="accent" class="[&_.iconify]:size-8" />
						<GsBtn label="Zurücksetzen" @click="resetAndClose" size="lg" icon="material-symbols:reset-settings" variant="solid" color="primary" class="[&_.iconify]:size-8" />
					</div>
				</div>
			</template>
		</UModal>
	</section>
</template>
